// FILE: src/pages/admin/AdminCurriculumValidationPage.jsx
// A curriculum checked against the structure MTO prescribes (WP-5; matrix
// 0.1, 0.3, 0.4, 3.2, 3.3, 4.4): each check with its clause and its result
// as icon + words (Passes / Fails / Not checked), the totals, the
// hour-by-hour table (an hour is each successive 60 minutes of core content
// in course order) and the assessment → learning outcome (CC code) map.
// Opening the page runs the check; the server stores the result.
//
//   GET /api/admin/curricula/:id            name, version, status
//   GET /api/admin/curricula/:id/validate   { ok, totals, checks, hours, iloMap, checkedAt }
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Link,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { DetailList, EmptyState, ErrorState, LoadingState, PageHeader, Section, StatusChip } from "../../components/ui";
import { ResponsiveTable } from "../../components/admin/AdminBits";
import { CheckStatusChip, CurriculumStatusChip } from "../../components/admin/ContentBits";
import { curriculumName, formatSeconds } from "../../components/admin/content";
import { formatDateTime, formatNumber } from "../../components/admin/format";
import { minutesText } from "./AdminCurriculaPage";
import { errorCode, messageFor } from "../../i18n/errors";
import { challengeTypeLabel } from "../../i18n/labels";

const LIST = "/admin/curricula";

/** The checks in the order MTO lists them, in words. */
export const CHECK_NAMES = {
  coreMinutes: "At least 20 hours (1,200 minutes) of core lessons",
  interactivePerHour: "An interactive activity in every hour",
  assessmentPerHour: "An assessment in every hour",
  videoLength: "Every video 7 minutes or shorter",
  videoPerHour: "At most 20 minutes of video in any hour",
  assessmentIlos: "Every assessment item mapped to a learning outcome (CC code)"
};
const CHECK_ORDER = Object.keys(CHECK_NAMES);

const hoursList = (list) => {
  const h = (list || []).map(String);
  if (h.length <= 1) return `hour ${h.join("")}`;
  return `hours ${h.slice(0, -1).join(", ")} and ${h[h.length - 1]}`;
};

/** One sentence (or a short list) explaining a check's result. */
export function checkDetail(key, check, totals) {
  if (!check) return "";
  if (check.status === "NOT_CHECKED") {
    return "Not checked: no video lengths have been entered for this curriculum. Enter each video on its lesson's slides page.";
  }
  switch (key) {
    case "coreMinutes":
      return `${minutesText(check.totalCoreMinutes ?? totals?.coreMinutes)} of core lessons; ${formatNumber(check.requiredMinutes || 1200)} minutes needed.`;
    case "interactivePerHour":
      return check.failingHours?.length
        ? `No interactive activity in ${hoursList(check.failingHours)}.`
        : "Every hour has at least one interactive activity.";
    case "assessmentPerHour":
      return check.failingHours?.length ? `No assessment item in ${hoursList(check.failingHours)}.` : "Every hour has at least one assessment item.";
    case "videoLength":
      return check.tooLong?.length
        ? check.tooLong.map((v) => `${v.title}${v.mediaTitle ? `, "${v.mediaTitle}"` : ""}${v.slideIndex ? ` (slide ${v.slideIndex})` : ""}: ${formatSeconds(v.durationSec)}`)
        : "Every video is 7 minutes or shorter.";
    case "videoPerHour":
      return check.failingHours?.length ? `More than 20 minutes of video in ${hoursList(check.failingHours)}.` : "No hour has more than 20 minutes of video.";
    case "assessmentIlos": {
      const n = check.unmapped?.length || 0;
      return n
        ? `${n} assessment ${n === 1 ? "item has" : "items have"} no CC code. They are marked in the learning outcome map below.`
        : "Every assessment item maps to at least one learning outcome.";
    }
    default:
      return "";
  }
}

/** Why an hour doesn't meet the rules, in words ("" when it does). */
export function hourProblems(h, videoChecked) {
  const out = [];
  if (!h.interactive) out.push("no interactive activity");
  if (!h.assessments) out.push("no assessment");
  if (videoChecked && h.videoSec > 20 * 60) out.push("more than 20 minutes of video");
  return out.join(", ");
}

export default function AdminCurriculumValidationPage() {
  const { id } = useParams();
  const [curriculum, setCurriculum] = useState(null);
  const [state, setState] = useState({ result: null, loading: true, error: null });
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const latest = useRef(0);

  const check = useCallback(() => {
    const req = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth(`/api/admin/curricula/${encodeURIComponent(id)}/validate`)
      .then((result) => {
        if (req === latest.current) setState({ result, loading: false, error: null });
      })
      .catch((error) => {
        if (req === latest.current) setState((s) => ({ ...s, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [id]);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    fetchWithAuth(`/api/admin/curricula/${encodeURIComponent(id)}`)
      .then(setCurriculum)
      .catch(() => setCurriculum(null));
  }, [id]);

  const { result, loading, error } = state;
  const name = curriculum ? curriculumName(curriculum) : result ? `Version ${result.version}` : "Curriculum";
  const breadcrumbs = [
    { label: "Curricula", to: LIST },
    { label: name, to: `${LIST}/${id}` },
    { label: "MTO check" }
  ];

  if (!result) {
    if (loading) return <LoadingState variant="page" label="Checking the curriculum…" />;
    const missing = ["CURRICULUM_NOT_FOUND", "NOT_FOUND"].includes(errorCode(error));
    return (
      <Box>
        <PageHeader title="MTO check" breadcrumbs={[{ label: "Curricula", to: LIST }, { label: "MTO check" }]} />
        {missing ? (
          <EmptyState
            icon={AccountTreeOutlinedIcon}
            title={messageFor("CURRICULUM_NOT_FOUND")}
            description="Go back to the list and choose another curriculum."
            action={
              <Button component={RouterLink} to={LIST} variant="contained">
                Back to curricula
              </Button>
            }
          />
        ) : (
          <ErrorState error={error} title="We couldn't check this curriculum" onRetry={check} />
        )}
      </Box>
    );
  }

  const checks = result.checks || {};
  const failing = CHECK_ORDER.filter((k) => checks[k]?.status === "FAIL");
  const notChecked = CHECK_ORDER.filter((k) => checks[k]?.status === "NOT_CHECKED");
  const videoChecked = checks.videoPerHour && checks.videoPerHour.status !== "NOT_CHECKED";
  const hours = Array.isArray(result.hours) ? result.hours : [];
  const iloMap = Array.isArray(result.iloMap) ? result.iloMap : [];
  const unmappedCount = iloMap.filter((r) => !(r.ilos || []).length).length;
  const shownMap = onlyUnmapped ? iloMap.filter((r) => !(r.ilos || []).length) : iloMap;

  return (
    <Box>
      <PageHeader
        eyebrow="MTO check"
        title={`Checking ${name}`}
        breadcrumbs={breadcrumbs}
        description="The structure the MTO Digital Learning Standard prescribes, checked from the lessons and slides of this curriculum. An hour is each 60 minutes of core lessons in course order; practice activities and homelinks don't count."
        actions={
          <>
            <Button component={RouterLink} to={`${LIST}/${id}`} variant="outlined">
              Back to the curriculum
            </Button>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={check} disabled={loading}>
              Check again
            </Button>
          </>
        }
      >
        {curriculum && (
          <Box sx={{ mt: 1.5 }}>
            <CurriculumStatusChip status={curriculum.status} />
          </Box>
        )}
      </PageHeader>

      <Box sx={{ height: 8, mb: 1 }}>{loading && <LinearProgress aria-label="Checking the curriculum again" />}</Box>
      {error && <ErrorState compact error={error} onRetry={check} />}

      <Alert
        severity={failing.length ? "error" : "success"}
        icon={failing.length ? <HighlightOffIcon /> : <CheckCircleOutlineIcon />}
        sx={{ mb: 2.5 }}
        data-testid="validation-result"
      >
        <AlertTitle>
          {failing.length
            ? `${failing.length} of ${CHECK_ORDER.length} checks fail`
            : notChecked.length
              ? "Every check that could be run passes"
              : "Every check passes"}
        </AlertTitle>
        {failing.length
          ? "This curriculum can't be approved until every check passes. The details are below."
          : notChecked.length
            ? `${notChecked.length} ${notChecked.length === 1 ? "check was" : "checks were"} not run because some information is missing.`
            : "This curriculum meets the structure MTO prescribes."}
        <Typography sx={{ mt: 0.5 }}>Checked {formatDateTime(result.checkedAt)}.</Typography>
      </Alert>

      <Stack spacing={2.5}>
        <Section id="validation-totals" title="Totals">
          <DetailList
            columns={3}
            items={[
              { label: "Core lessons", value: minutesText(result.totals?.coreMinutes) },
              { label: "Hours of core lessons", value: `${formatNumber(result.totals?.coreHours, 2)} h` },
              { label: "Homelinks", value: minutesText(result.totals?.flexMinutes) }
            ]}
          />
        </Section>

        <Section id="validation-checks" title="Checks">
          <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {CHECK_ORDER.filter((k) => checks[k]).map((k) => {
              const c = checks[k];
              const detail = checkDetail(k, c, result.totals);
              return (
                <Box
                  component="li"
                  key={k}
                  data-testid={`check-${k}`}
                  sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3 }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between" }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography component="h3" sx={{ fontWeight: 650 }}>
                        {CHECK_NAMES[k]}
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }}>MTO {c.clause}</Typography>
                    </Box>
                    <CheckStatusChip status={c.status} />
                  </Stack>
                  {Array.isArray(detail) ? (
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                      {detail.map((d) => (
                        <li key={d}>{d}</li>
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ mt: 1 }}>{detail}</Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Section>

        <Section
          id="validation-hours"
          title="Hour by hour"
          description="Each hour needs at least one interactive activity and one assessment item (MTO CD3.3c–d), and at most 20 minutes of video (MTO 3.5)."
          contentSx={{ p: { xs: 2, sm: 3 } }}
        >
          {hours.length === 0 ? (
            <EmptyState compact title="No core lessons yet" description="Add chapters with slides to this curriculum, then check again." />
          ) : (
            <ResponsiveTable label="Hour by hour" minWidth={860}>
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col">
                    Hour
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Minutes
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    Interactive activities
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    Assessment items
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    Video
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Meets the rules
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Lessons
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hours.map((h) => {
                  const problems = hourProblems(h, videoChecked);
                  return (
                    <TableRow key={h.hour}>
                      <TableCell component="th" scope="row">
                        Hour {h.hour}
                        {h.partial ? " (part)" : ""}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatNumber(h.fromMinute)}–{formatNumber(h.toMinute, h.toMinute % 1 ? 1 : 0)}
                      </TableCell>
                      <TableCell align="right">{h.interactive}</TableCell>
                      <TableCell align="right">{h.assessments}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {videoChecked ? formatSeconds(h.videoSec) : "Not entered"}
                      </TableCell>
                      <TableCell>
                        {problems ? (
                          <StatusChip tone="error" icon={<HighlightOffIcon />} label={`No: ${problems}`} />
                        ) : (
                          <StatusChip tone="success" icon={<CheckCircleOutlineIcon />} label="Yes" />
                        )}
                      </TableCell>
                      <TableCell>{(h.resources || []).join(", ") || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </ResponsiveTable>
          )}
        </Section>

        <Section
          id="validation-ilos"
          title="Learning outcome map"
          description={`Every graded item and the learning outcomes (CC codes) it assesses (MTO CD4.1–4.2). ${
            unmappedCount ? `${unmappedCount} ${unmappedCount === 1 ? "item has" : "items have"} no CC code.` : "Every item has a CC code."
          } Codes come from the slide's CC code, or the lesson's learning outcomes for a graded assessment.`}
          contentSx={{ p: { xs: 2, sm: 3 } }}
        >
          {iloMap.length === 0 ? (
            <EmptyState compact title="No graded items yet" description="Quiz and matching slides, and graded assessments, appear here." />
          ) : (
            <>
              {unmappedCount > 0 && (
                <FormControlLabel
                  control={<Checkbox checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} />}
                  label="Show only items without a CC code"
                  sx={{ mb: 1 }}
                />
              )}
              <ResponsiveTable label="Learning outcome map" minWidth={720}>
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col">
                      Lesson
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Slide
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Activity
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Learning outcomes
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shownMap.map((r, i) => (
                    <TableRow key={`${r.resourceId}-${r.slideIndex ?? "a"}-${i}`}>
                      <TableCell component="th" scope="row">
                        <Link component={RouterLink} to={`/admin/resources/${r.resourceId}`}>
                          {r.title}
                        </Link>
                      </TableCell>
                      <TableCell>{r.slideIndex ? `${r.slideIndex}. ${r.slideName}` : "Whole assessment"}</TableCell>
                      <TableCell>{r.challengeType && r.challengeType !== "NONE" ? challengeTypeLabel(r.challengeType) : "Graded assessment"}</TableCell>
                      <TableCell>
                        {(r.ilos || []).length ? (
                          r.ilos.join(", ")
                        ) : (
                          <StatusChip tone="warning" icon={<HighlightOffIcon />} label="No CC code" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveTable>
            </>
          )}
        </Section>
      </Stack>
    </Box>
  );
}
